import React, { useEffect, useMemo, useState } from 'react';
import ChildCareOutlinedIcon from '@mui/icons-material/ChildCareOutlined';
import styles from './motorMilestones.module.css';
import parts from '../moduleCard/moduleParts.module.css';
import CustomButton from '../../../../components/buttons/CustomButton';
import ModuleCard from '../moduleCard/ModuleCard';
import ModulePdfButton from '../moduleCard/ModulePdfButton';
import ConfirmPopup from '../../../../popup/ConfirmPopup/ConfirmPopup';
import MotorMilestonePopup from '../../../../popup/MotorMilestonePopup/MotorMilestonePopup';
import BirthInfoPopup from '../../../../popup/MotorMilestonePopup/BirthInfoPopup';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import ModuleReportTemplate from '../../../../components/documents-templates/report/ModuleReportTemplate';
import MotorMilestonesPanel from './MotorMilestonesPanel';
import MotorMilestonesTable from './MotorMilestonesTable';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { MODULE_KEYS } from '../../modules/appointmentModules';
import { REPORT_SECTIONS, buildMotorMilestonesJob } from '../../modules/reportSections';
import { WHO_MOTOR_ATTRIBUTION, gestationalLabel, hasValidBirthday } from '../../modules/motorMilestones/motorMilestones';
import { formatAge } from '../../modules/growth/growthIndicators';

const SECTION = REPORT_SECTIONS[MODULE_KEYS.MOTOR_MILESTONES];

/**
 * Módulo "Marcos motores": data em que o bebê conquistou cada marco, sobre as
 * janelas de conquista da OMS, com idade corrigida para prematuros.
 *
 * `data`: marcos registrados do paciente (de qualquer atendimento).
 * `birthInfo`: idade gestacional ao nascer (patient_birth_info) ou null.
 * `birthday`: do cadastro, base da idade.
 * `enabled`: false na aba Saúde do paciente (só leitura, mas ainda exporta).
 */
const MotorMilestones = ({
  data,
  birthInfo,
  patientId,
  appointmentId,
  appointmentDate,
  birthday,
  enabled,
  collapsible = false,
  onUpdated,
  patientName,
  professionalId,
  companyConfig,
}) => {
  const { DeletePatientMotorMilestone } = DataProviderInstance();
  const [records, setRecords] = useState(Array.isArray(data) ? data : []);
  const [popup, setPopup] = useState(null);
  const [showBirthInfo, setShowBirthInfo] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    setRecords(Array.isArray(data) ? data : []);
  }, [data]);

  // Mesmo com nenhum marco registrado, a lista mostra o que esperar para a idade
  const job = useMemo(
    () => buildMotorMilestonesJob(records, { birthday }, birthInfo, { allowEmpty: true }),
    [records, birthday, birthInfo]
  );
  const gestational = gestationalLabel(birthInfo);

  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig,
    reportType: SECTION.reportType,
    prepare: () => buildMotorMilestonesJob(records, { birthday }, birthInfo),
  });

  function reload(needsReload) {
    if (needsReload && onUpdated) onUpdated();
  }

  function confirmDelete() {
    const record = deleting;
    setDeleting(null);
    DeletePatientMotorMilestone(record.id, (response) => {
      reload(response && response.status === 'SUCCESS');
    });
  }

  const actions = records.length > 0 ? <ModulePdfButton report={report} /> : null;

  return (
    <>
      <ModuleCard
        accent="teal"
        title="Marcos motores"
        icon={<ChildCareOutlinedIcon />}
        description="Conquistas motoras do bebê nas janelas da OMS"
        actions={actions}
        collapsible={collapsible}
      >
        <div className={styles.content}>
          {!hasValidBirthday(birthday) && (
            <div className={styles.warning}>
              Para calcular a idade e comparar com as janelas da OMS, informe a data de nascimento no cadastro do paciente.
            </div>
          )}

          <div className={styles.ages}>
            {job.current && <span className={parts.chip}>Idade: {formatAge(job.current.chronological)}</span>}
            {job.current && job.current.corrected != null && (
              <span className={`${parts.chip} ${parts.chipAccent}`}>Idade corrigida: {formatAge(job.current.corrected)}</span>
            )}
            <span className={parts.chip}>
              {gestational ? `Idade gestacional ao nascer: ${gestational}` : 'Idade gestacional não informada'}
            </span>
            {enabled && (
              <CustomButton
                variant="outlined"
                style="secondary"
                label={gestational ? 'Alterar' : 'Informar'}
                onClick={() => setShowBirthInfo(true)}
              />
            )}
          </div>

          <MotorMilestonesPanel rows={job.rows} current={job.current} corrected={job.corrected} />

          <MotorMilestonesTable
            rows={job.rows}
            enabled={enabled}
            onRegister={(milestone, record) => setPopup({ milestone, record })}
            onDelete={setDeleting}
          />
          <span className={styles.attribution}>{WHO_MOTOR_ATTRIBUTION}</span>
        </div>
      </ModuleCard>

      {report.job && (
        <HiddenReport>
          <ModuleReportTemplate ref={report.ref} section={SECTION} job={report.job} {...report.paperProps} />
        </HiddenReport>
      )}

      {popup && (
        <MotorMilestonePopup
          patientId={patientId}
          appointmentId={appointmentId}
          appointmentDate={appointmentDate}
          milestone={popup.milestone}
          record={popup.record}
          birthday={birthday}
          birthInfo={birthInfo}
          OnHide={(needsReload) => { setPopup(null); reload(needsReload); }}
        />
      )}

      {showBirthInfo && (
        <BirthInfoPopup
          patientId={patientId}
          birthInfo={birthInfo}
          OnHide={(needsReload) => { setShowBirthInfo(false); reload(needsReload); }}
        />
      )}

      {deleting && (
        <ConfirmPopup
          Visibility={true}
          Title="Confirmar exclusão"
          Description="Tem certeza que deseja excluir o registro deste marco? Ele volta a ficar pendente."
          ConfirmLabel="Excluir"
          OnConfirm={confirmDelete}
          OnHide={() => setDeleting(null)}
        />
      )}
    </>
  );
};

export default MotorMilestones;
